
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Shield } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface BadgeType {
  name: string;
  description: string;
  icon_url: string;
}

interface UserBadge {
  id: string;
  badge_id: string;
  is_displayed: boolean;
  badges: BadgeType;
}

export const ProfileBadges = ({ userId, isOwnProfile }: { userId: string; isOwnProfile: boolean }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: badges, isLoading } = useQuery({
    queryKey: ["user-badges", userId],
    queryFn: async () => {
      console.log("Fetching badges for user:", userId);
      const { data, error } = await supabase
        .from("user_badges")
        .select(`
          id,
          badge_id,
          is_displayed,
          badges (
            name,
            description,
            icon_url
          )
        `)
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching badges:", error);
        throw error;
      }
      console.log("Fetched badges:", data);
      return data as unknown as UserBadge[];
    },
    enabled: !!userId,
  });

  const toggleBadgeDisplay = useMutation({
    mutationFn: async ({ badgeId, isDisplayed }: { badgeId: string; isDisplayed: boolean }) => {
      const { error } = await supabase
        .from("user_badges")
        .update({ is_displayed: isDisplayed })
        .eq("id", badgeId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-badges"] });
      toast({
        title: "Success",
        description: "Badge display preference updated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return <div className="p-4">Loading badges...</div>;
  }

  if (!badges?.length) {
    return (
      <div className="text-center p-8 text-gray-400">
        <Shield className="w-12 h-12 mx-auto mb-4" />
        <p>No badges earned yet</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      {badges.map((badge) => (
        <div
          key={badge.id}
          className="flex items-center justify-between p-4 rounded-lg bg-flux-black/50 border border-flux-cyan/20 hover:bg-flux-black/70 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-flux-cyan" />
            <div>
              <Badge 
                variant={badge.badges.name === "Pioneer" ? "pioneer" : "default"}
                className="mb-1"
              >
                {badge.badges.name}
              </Badge>
              <p className="text-sm text-gray-400">{badge.badges.description}</p>
            </div>
          </div>
          {isOwnProfile && (
            <Switch
              checked={badge.is_displayed}
              onCheckedChange={(checked) =>
                toggleBadgeDisplay.mutate({ badgeId: badge.id, isDisplayed: checked })
              }
            />
          )}
        </div>
      ))}
    </div>
  );
};
